import "./style.css";
import { formatC, lerpC, type Complex } from "./complex";
import { FractalGL } from "./gl";
import { cardioidPoint, criticalOrbit, juliaKind } from "./iterate";
import { PRESETS } from "./presets";
import {
  JULIA_VIEW,
  MANDEL_VIEW,
  cloneView,
  complexToPixel,
  framePoint,
  panBy,
  pixelToComplex,
  zoomAt,
  type View,
} from "./view";

const START: Complex = { re: -0.12, im: 0.16 };
const HANDLE_STEP = 0.02;
const HANDLE_STEP_FINE = 0.002;

const el = {
  mandel: document.querySelector<HTMLCanvasElement>("#mandel")!,
  julia: document.querySelector<HTMLCanvasElement>("#julia")!,
  handle: document.querySelector<HTMLButtonElement>("#handle")!,
  orbit: document.querySelector<SVGSVGElement>("#orbit")!,
  cReadout: document.querySelector<HTMLElement>("#c-readout")!,
  kind: document.querySelector<HTMLElement>("#kind")!,
  kindHint: document.querySelector<HTMLElement>("#kind-hint")!,
  presets: document.querySelector<HTMLElement>("#presets")!,
  presetHint: document.querySelector<HTMLElement>("#preset-hint")!,
  demo: document.querySelector<HTMLButtonElement>("#demo")!,
  iters: document.querySelector<HTMLInputElement>("#iters")!,
  itersVal: document.querySelector<HTMLElement>("#iters-val")!,
  live: document.querySelector<HTMLElement>("#live")!,
};

if (!el.mandel || !el.julia) {
  throw new Error("Julia Morph: missing canvases");
}

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const state = {
  c: { ...START },
  maxIter: 120,
  mandelView: cloneView(MANDEL_VIEW),
  juliaView: cloneView(JULIA_VIEW),
  activePreset: "inside" as string | null,
  announceTimer: 0,
};

let mandelGL: FractalGL | null = null;
let juliaGL: FractalGL | null = null;
let glOk = true;
let demoRaf = 0;

function currentKind(): "lace" | "dust" {
  return juliaKind(state.c, Math.max(state.maxIter, 240));
}

function setReadout(): void {
  const kind = currentKind();
  el.cReadout.textContent = `c = ${formatC(state.c)}`;
  el.kind.textContent = kind === "lace" ? "LACE" : "DUST";
  el.kind.dataset.kind = kind;
  el.kindHint.textContent =
    kind === "lace"
      ? "Connected. The critical orbit stays home."
      : "Cantor dust. The critical orbit has left.";
}

function announce(): void {
  window.clearTimeout(state.announceTimer);
  state.announceTimer = window.setTimeout(() => {
    const kind = currentKind();
    el.live.textContent = `${formatC(state.c)}. Julia is ${kind === "lace" ? "connected lace" : "Cantor dust"}.`;
  }, 220);
}

function placeHandle(): void {
  const pane = el.mandel.parentElement!;
  const w = pane.clientWidth;
  const h = pane.clientHeight;
  const p = complexToPixel(state.c, w, h, state.mandelView);
  el.handle.style.left = `${p.x}px`;
  el.handle.style.top = `${p.y}px`;
}

function drawOrbit(): void {
  const svg = el.orbit;
  const w = svg.clientWidth || el.julia.clientWidth;
  const h = svg.clientHeight || el.julia.clientHeight;
  svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
  const pts = criticalOrbit(state.c, 28);
  const coords = pts
    .map((z) => complexToPixel(z, w, h, state.juliaView))
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  const d = coords.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const dots = coords
    .slice(0, 12)
    .map(
      (p, i) =>
        `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="${i === 0 ? 3.2 : 2.1}" fill="${i === 0 ? "#f3ead6" : "#c6452a"}" stroke="#8c2e1c" stroke-width="0.8"/>`,
    )
    .join("");
  svg.innerHTML = `<path d="${d}" fill="none" stroke="#c6452a" stroke-width="1.4" stroke-linejoin="round" stroke-linecap="round" opacity="0.9"/>${dots}`;
}

function render(): void {
  if (!glOk || !mandelGL || !juliaGL) return;
  mandelGL.draw({ view: state.mandelView, c: state.c, maxIter: state.maxIter, julia: false });
  juliaGL.draw({ view: state.juliaView, c: state.c, maxIter: state.maxIter, julia: true });
  placeHandle();
  drawOrbit();
  setReadout();
}

function setC(next: Complex, fromPreset: string | null = null): void {
  state.c = { re: next.re, im: next.im };
  state.activePreset = fromPreset;
  syncPresetPressed();
  const pane = el.mandel.parentElement!;
  const aspect = pane.clientWidth / Math.max(pane.clientHeight, 1);
  state.mandelView = framePoint(state.mandelView, state.c, aspect);
  render();
  announce();
}

function syncPresetPressed(): void {
  for (const btn of el.presets.querySelectorAll<HTMLButtonElement>(".chip")) {
    btn.setAttribute("aria-pressed", btn.dataset.id === state.activePreset ? "true" : "false");
  }
}

function buildPresets(): void {
  const frag = document.createDocumentFragment();
  for (const p of PRESETS) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "chip";
    b.dataset.id = p.id;
    b.textContent = p.name;
    b.setAttribute("aria-pressed", p.id === state.activePreset ? "true" : "false");
    b.addEventListener("click", () => {
      el.presetHint.textContent = p.hint;
      setC(p.c, p.id);
    });
    frag.append(b);
  }
  el.presets.append(frag);
}

function eventToC(ev: PointerEvent): Complex {
  const rect = el.mandel.getBoundingClientRect();
  return pixelToComplex(ev.clientX - rect.left, ev.clientY - rect.top, rect.width, rect.height, state.mandelView);
}

function bindMandelPointer(): void {
  let dragging = false;
  el.mandel.addEventListener("pointerdown", (ev) => {
    if (ev.button === 1 || ev.altKey) return;
    dragging = true;
    el.mandel.setPointerCapture(ev.pointerId);
    setC(eventToC(ev), null);
  });
  el.mandel.addEventListener("pointermove", (ev) => {
    if (!dragging) return;
    setC(eventToC(ev), null);
  });
  const stop = () => {
    dragging = false;
  };
  el.mandel.addEventListener("pointerup", stop);
  el.mandel.addEventListener("pointercancel", stop);

  let panning = false;
  let last: { x: number; y: number } | null = null;
  el.mandel.addEventListener("pointerdown", (ev) => {
    if (ev.button !== 1 && !ev.altKey) return;
    panning = true;
    last = { x: ev.clientX, y: ev.clientY };
    el.mandel.setPointerCapture(ev.pointerId);
    ev.preventDefault();
  });
  el.mandel.addEventListener("pointermove", (ev) => {
    if (!panning || !last) return;
    const rect = el.mandel.getBoundingClientRect();
    const a = pixelToComplex(last.x - rect.left, last.y - rect.top, rect.width, rect.height, state.mandelView);
    const b = pixelToComplex(ev.clientX - rect.left, ev.clientY - rect.top, rect.width, rect.height, state.mandelView);
    state.mandelView = panBy(state.mandelView, a.re - b.re, a.im - b.im);
    last = { x: ev.clientX, y: ev.clientY };
    render();
  });
  el.mandel.addEventListener("pointerup", () => {
    panning = false;
    last = null;
  });
}

function bindHandle(): void {
  let dragging = false;
  el.handle.addEventListener("pointerdown", (ev) => {
    dragging = true;
    el.handle.setPointerCapture(ev.pointerId);
    ev.preventDefault();
    setC(eventToC(ev), null);
  });
  el.handle.addEventListener("pointermove", (ev) => {
    if (!dragging) return;
    setC(eventToC(ev), null);
  });
  el.handle.addEventListener("pointerup", () => {
    dragging = false;
  });
  el.handle.addEventListener("keydown", (ev) => {
    const step = ev.shiftKey ? HANDLE_STEP_FINE : HANDLE_STEP;
    let dre = 0;
    let dim = 0;
    if (ev.key === "ArrowLeft") dre = -step;
    else if (ev.key === "ArrowRight") dre = step;
    else if (ev.key === "ArrowUp") dim = step;
    else if (ev.key === "ArrowDown") dim = -step;
    else return;
    ev.preventDefault();
    setC({ re: state.c.re + dre, im: state.c.im + dim }, null);
  });
}

function bindZoom(canvas: HTMLCanvasElement, which: "mandel" | "julia"): void {
  canvas.addEventListener(
    "wheel",
    (ev) => {
      ev.preventDefault();
      const view = which === "mandel" ? state.mandelView : state.juliaView;
      const rect = canvas.getBoundingClientRect();
      const focus = pixelToComplex(ev.clientX - rect.left, ev.clientY - rect.top, rect.width, rect.height, view);
      const factor = ev.deltaY > 0 ? 1.12 : 1 / 1.12;
      const next = zoomAt(view, focus, factor);
      if (which === "mandel") state.mandelView = next;
      else state.juliaView = next;
      render();
    },
    { passive: false },
  );
  canvas.addEventListener("dblclick", () => {
    if (which === "mandel") state.mandelView = cloneView(MANDEL_VIEW);
    else state.juliaView = cloneView(JULIA_VIEW);
    render();
  });
}

function bindJuliaPan(): void {
  let panning = false;
  let last: { x: number; y: number } | null = null;
  el.julia.addEventListener("pointerdown", (ev) => {
    panning = true;
    last = { x: ev.clientX, y: ev.clientY };
    el.julia.setPointerCapture(ev.pointerId);
  });
  el.julia.addEventListener("pointermove", (ev) => {
    if (!panning || !last) return;
    const rect = el.julia.getBoundingClientRect();
    const a = pixelToComplex(last.x - rect.left, last.y - rect.top, rect.width, rect.height, state.juliaView);
    const b = pixelToComplex(ev.clientX - rect.left, ev.clientY - rect.top, rect.width, rect.height, state.juliaView);
    state.juliaView = panBy(state.juliaView, a.re - b.re, a.im - b.im);
    last = { x: ev.clientX, y: ev.clientY };
    render();
  });
  const stop = () => {
    panning = false;
    last = null;
  };
  el.julia.addEventListener("pointerup", stop);
  el.julia.addEventListener("pointercancel", stop);
}

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

function playBoundary(): void {
  cancelAnimationFrame(demoRaf);
  const theta = 2.92;
  const from = cardioidPoint(theta, 1.16);
  const to = cardioidPoint(theta, 0.68);
  el.presetHint.textContent =
    "Across the cardioid wall near the seahorse: dust tightens, then the orbit comes home and the set turns to lace.";
  if (reduceMotion) {
    setC(to, null);
    return;
  }
  const start = performance.now();
  const dur = 2600;
  const tick = (now: number) => {
    const t = Math.min(1, (now - start) / dur);
    setC(lerpC(from, to, easeInOut(t)), null);
    if (t < 1) demoRaf = requestAnimationFrame(tick);
  };
  demoRaf = requestAnimationFrame(tick);
}

function boot(): void {
  buildPresets();
  try {
    mandelGL = new FractalGL(el.mandel);
    juliaGL = new FractalGL(el.julia);
  } catch (err) {
    glOk = false;
    const msg = document.createElement("p");
    msg.className = "fallback";
    msg.textContent = err instanceof Error ? err.message : "WebGL failed to start.";
    el.mandel.replaceWith(msg);
    return;
  }
  bindMandelPointer();
  bindHandle();
  bindZoom(el.mandel, "mandel");
  bindZoom(el.julia, "julia");
  bindJuliaPan();
  el.demo.addEventListener("click", playBoundary);
  el.iters.addEventListener("input", () => {
    state.maxIter = Number(el.iters.value);
    el.itersVal.textContent = String(state.maxIter);
    render();
  });
  const ro = new ResizeObserver(() => render());
  ro.observe(el.mandel.parentElement!);
  ro.observe(el.julia.parentElement!);
  render();
}

boot();
